import { useState } from "react";

const STATUS_STYLE = {
    FROZEN: { color: "var(--amber)", label: "FROZEN" },
    OPEN: { color: "var(--amber)", label: "AWAITING RESPONSE" },
    PROVIDED: { color: "var(--forest)", label: "PROVIDED" },
    SATISFIED: { color: "var(--forest)", label: "SATISFIED" },
    EXCEPTION: { color: "var(--crimson)", label: "CAPACITY EXCEPTION" },
};

const Badge = ({ status }) => {
    const s = STATUS_STYLE[status] || STATUS_STYLE.OPEN;
    return (
        <span
            className="mono-label border px-2 py-0.5"
            style={{ color: s.color, borderColor: s.color }}
        >
            {s.label}
        </span>
    );
};

const PartForm = ({ gate, part, state, onRespond, disabled }) => {
    const [values, setValues] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const provided = state.status === "PROVIDED";

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await onRespond(gate.id, part.id, values);
        } catch (e) {
            setError(e?.response?.data?.detail || "Response could not be recorded.");
        }
        setBusy(false);
    };

    if (provided) {
        return (
            <div className="mt-3 panel-alt px-4 py-3" data-testid={`gate-${gate.id}-${part.id}-provided`}>
                <p className="mono-label mb-2">{part.role} — bounded response recorded</p>
                {Object.entries(state.response || {}).map(([k, v]) => (
                    <p key={k} className="text-sm" style={{ color: "var(--ink-soft)" }}>
                        <span className="mono-data" style={{ fontSize: 11 }}>{k}</span> — {v}
                    </p>
                ))}
            </div>
        );
    }

    return (
        <div className="mt-3 border hairline bg-white px-4 py-4">
            <div className="flex items-center justify-between gap-3">
                <p className="mono-label">{part.role}</p>
                {state.status === "EXCEPTION" && <Badge status="EXCEPTION" />}
            </div>
            <div className="mt-3 grid gap-3">
                {part.fields.map((f) => (
                    <div key={f.id}>
                        <label className="mono-label" style={{ fontSize: 10 }}>
                            {f.label}
                            {f.required === false ? "" : " *"}
                        </label>
                        {f.type === "select" ? (
                            <select
                                data-testid={`gate-${gate.id}-${part.id}-${f.id}`}
                                className="mt-1"
                                value={values[f.id] || ""}
                                onChange={(e) => setValues({ ...values, [f.id]: e.target.value })}
                            >
                                <option value="">— select —</option>
                                {f.options.map((o) => (
                                    <option key={o} value={o}>{o}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                data-testid={`gate-${gate.id}-${part.id}-${f.id}`}
                                className="mt-1"
                                maxLength={f.max_len || 120}
                                placeholder={f.placeholder || ""}
                                value={values[f.id] || ""}
                                onChange={(e) => setValues({ ...values, [f.id]: e.target.value })}
                            />
                        )}
                    </div>
                ))}
            </div>
            {error && (
                <p className="mt-2 text-sm" style={{ color: "var(--crimson)" }} data-testid={`gate-${gate.id}-${part.id}-error`}>
                    {error}
                </p>
            )}
            <button
                data-testid={`gate-${gate.id}-${part.id}-submit`}
                className="btn-accent mt-4"
                onClick={submit}
                disabled={busy || disabled}
            >
                {busy ? "Recording…" : "Submit bounded response"}
            </button>
        </div>
    );
};

export const GateCard = ({ gate, state, index, onRespond, disabled }) => (
    <section className="panel px-5 py-5" data-testid={`gate-card-${gate.id}`}>
        <div className="flex items-start justify-between gap-3">
            <div>
                <p className="mono-label">Gate {String(index + 1).padStart(2, "0")} · {gate.boundary}</p>
                <h3 className="font-serif mt-1" style={{ fontSize: 21 }}>{gate.title}</h3>
            </div>
            <Badge status={state.status} />
        </div>
        <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
            {gate.freeze_reason}
        </p>
        {gate.parts.map((p) => (
            <PartForm
                key={p.id}
                gate={gate}
                part={p}
                state={state.parts[p.id]}
                onRespond={onRespond}
                disabled={disabled}
            />
        ))}
    </section>
);
